/**
 * MyDayFocus.js
 *
 * The prioritised part of the "Mai napom" briefing (rendered inside {@link module:components/management/MyDayBriefingModal}):
 * - "Most ezzel foglalkozz": the top of ONE ranked list — fixed appointments (calendar events, timed to-dos) first, then
 *   overdue / urgent work, important unanswered mail, projects, hot and stale leads and customers nobody has called for a while,
 *   each with the reasons why it is on the list;
 * - "AI napi terv": an explicit (credit-metered) AI plan over that list;
 * - "AI válasz" on mail rows: an AI reply draft shown inline; the user reviews it and opens it in the mail composer —
 *   nothing is ever sent from here;
 * - "Figyelmet igényel": the leads / projects / dormant customers behind the ranking.
 * Pure presentation + two user-triggered AI calls; opaque surfaces, light + dark via the management theme.
 *
 * @module components/management/MyDayFocus
 */

import React, {useCallback, useMemo, useState} from 'react';
import {ActivityIndicator, Pressable, StyleSheet, Text, View} from 'react-native';
import {useTranslation} from 'react-i18next';
import Icon from './SafeIcon';
import {SectionTitle} from './FollowUpAgendaKit';
import {BRAND, useManagementTheme, withAlpha} from '../../helpers/managementTheme';
import {describeApiError} from '../../helpers/apiError';
import {FOLLOW_UP_ICONS} from '../../config/leadContactMeta';
import {MANAGEMENT_DETAIL_ROUTE, SALES_CUSTOMER_DETAIL_ROUTE} from '../../config/salesRoutes';
import {workItemRoute, workKindMeta} from '../../helpers/myDayWorkItems';
import {TOP_LIMIT, draftComposeParams, rankPriorities, reasonText, toPlanItems} from '../../helpers/myDayFocus';
import {analyzeFocusPlan, draftMailReply} from '../../helpers/myDayFocusApi';

const SECTION_LIMIT = 4;
const SOURCE_ICONS = Object.freeze({
    event: 'calendar-clock',
    project: 'briefcase-outline',
    lead: 'account-search-outline',
    customer: 'account-clock-outline',
});

const fmtTime = (d) => (d ? new Date(d).toLocaleTimeString([], {hour: '2-digit', minute: '2-digit'}) : '');

const noCredits = (err) => err?.status === 402 || err?.status === 403;

const useSeverityColor = () => {
    const m = useManagementTheme();

    return (severity) => ({critical: m.colors.danger, high: BRAND.base, medium: m.colors.info}[severity] || m.colors.muted);
};

/** @returns {string} MCI icon of a ranked row. */
const iconOf = (row) => {
    if (row.source === 'work') {
        return workKindMeta(row.kind).icon;
    }

    if (row.source === 'followUp') {
        return FOLLOW_UP_ICONS[row.kind] || 'dots-horizontal';
    }

    return SOURCE_ICONS[row.source] || 'flag-outline';
};

/** Route of a ranked row, or null when no real screen exists (the row is then not tappable). */
export const rowRoute = (row) => {
    if (row.source === 'work') {
        return workItemRoute(row.ref);
    }

    if (row.source === 'lead') {
        return {name: MANAGEMENT_DETAIL_ROUTE, params: {type: 'leads', id: row.ref.id}};
    }

    if (row.source === 'customer') {
        return {name: SALES_CUSTOMER_DETAIL_ROUTE, params: {customerId: row.ref.id}};
    }

    return null;
};

/** Inline AI reply draft under a mail row. */
const DraftBox = ({state, onOpen, onRetry}) => {
    const {t} = useTranslation();
    const m = useManagementTheme();

    if (state.busy) {
        return (
            <View style={[styles.draft, {backgroundColor: m.colors.chipBg, borderColor: m.colors.border}]}>
                <ActivityIndicator size="small" color={BRAND.base} />
                <Text style={[styles.draftHint, {color: m.colors.subText}]}>{t('myday_ai_reply_busy')}</Text>
            </View>
        );
    }

    if (state.error) {
        return (
            <View style={[styles.draft, {backgroundColor: withAlpha(m.colors.danger, 0.1), borderColor: withAlpha(m.colors.danger, 0.4)}]}>
                <Text style={[styles.draftHint, {color: m.colors.danger}]}>{state.error}</Text>
                {!state.noCredits && (
                    <Pressable onPress={onRetry} accessibilityRole="button"><Text style={[styles.link, {color: BRAND.base}]}>{t('myday_retry')}</Text></Pressable>
                )}
            </View>
        );
    }

    if (!state.draft) {
        return null;
    }

    return (
        <View style={[styles.draft, {backgroundColor: m.colors.chipBg, borderColor: m.colors.border}]}>
            <Text style={[styles.draftLabel, {color: m.colors.subText}]}>{t('myday_ai_reply_label')}</Text>
            <Text style={[styles.draftSubject, {color: m.colors.heading}]} numberOfLines={2}>{state.draft.subject}</Text>
            <Text style={[styles.draftBody, {color: m.colors.text}]} numberOfLines={9}>{state.draft.body}</Text>
            <View style={styles.draftActions}>
                <Pressable onPress={onOpen} accessibilityRole="button" style={({pressed}) => [styles.draftCta, {backgroundColor: BRAND.base}, pressed && styles.pressed]}>
                    <Icon name="email-edit-outline" size={16} color="#FFFFFF" />
                    <Text style={styles.draftCtaText}>{t('myday_ai_reply_open')}</Text>
                </Pressable>
                <Pressable onPress={onRetry} accessibilityRole="button" hitSlop={8}><Text style={[styles.link, {color: BRAND.base}]}>{t('myday_ai_reply_again')}</Text></Pressable>
            </View>
            <Text style={[styles.draftHint, {color: m.colors.muted}]}>{t('myday_ai_reply_review')}</Text>
        </View>
    );
};

/** One ranked row: severity stripe, kind icon, title, reasons, time, optional AI reply. */
const FocusRow = ({row, onOpen, onReply, reply, onOpenDraft}) => {
    const {t} = useTranslation();
    const m = useManagementTheme();
    const color = useSeverityColor()(row.severity);
    const route = rowRoute(row);
    const isMail = row.source === 'work' && row.kind === 'mail';
    const reasons = row.reasons.map((r) => reasonText(t, r)).filter(Boolean);
    const time = row.allDay ? t('sfu_all_day') : (row.source === 'event' || row.source === 'followUp' || row.source === 'work' ? fmtTime(row.dueAt) : '');

    return (
        <View style={[styles.row, {backgroundColor: m.colors.surface, borderColor: m.colors.border}, m.shadow(1)]}>
            <View style={[styles.stripe, {backgroundColor: color}]} />
            <Pressable
                disabled={!route}
                onPress={() => route && onOpen(route)}
                accessibilityRole={route ? 'button' : undefined}
                style={({pressed}) => [styles.rowMain, pressed && styles.pressed]}
            >
                <View style={[styles.rowIcon, {backgroundColor: withAlpha(color, m.isDark ? 0.22 : 0.12)}]}>
                    <Icon name={iconOf(row)} size={20} color={color} />
                </View>
                <View style={{flex: 1}}>
                    <Text style={[styles.rowTitle, {color: m.colors.heading}]} numberOfLines={2}>{row.title || t('myday_mail_no_subject')}</Text>
                    {!!row.subtitle && <Text style={[styles.rowSub, {color: m.colors.subText}]} numberOfLines={1}>{row.subtitle}</Text>}
                    {(reasons.length > 0 || !!time) && (
                        <View style={styles.meta}>
                            {!!time && <Text style={[styles.time, {color: m.colors.subText}]}>{time}</Text>}
                            {reasons.slice(0, 2).map((text) => (
                                <View key={text} style={[styles.chip, {backgroundColor: withAlpha(color, m.isDark ? 0.22 : 0.12)}]}>
                                    <Text style={[styles.chipText, {color}]} numberOfLines={1}>{text}</Text>
                                </View>
                            ))}
                        </View>
                    )}
                </View>
                {!!route && <Icon name="chevron-right" size={20} color={m.colors.muted} />}
            </Pressable>
            {isMail && (
                <Pressable
                    onPress={onReply}
                    disabled={reply?.busy}
                    accessibilityRole="button"
                    accessibilityLabel={t('myday_ai_reply')}
                    style={({pressed}) => [styles.aiBtn, {borderColor: withAlpha(BRAND.base, 0.5)}, pressed && styles.pressed]}
                >
                    <Icon name="creation" size={15} color={BRAND.base} />
                    <Text style={[styles.aiBtnText, {color: BRAND.base}]}>{t('myday_ai_reply')}</Text>
                </Pressable>
            )}
            {isMail && !!reply && <DraftBox state={reply} onOpen={onOpenDraft} onRetry={onReply} />}
        </View>
    );
};

/** AI day-plan card (explicit, credit-metered). */
const PlanCard = ({ranked, plan, state, onRun}) => {
    const {t} = useTranslation();
    const m = useManagementTheme();
    const byKey = useMemo(() => new Map(ranked.map((r) => [r.key, r])), [ranked]);

    return (
        <View style={[styles.plan, {backgroundColor: m.colors.surface, borderColor: withAlpha(BRAND.base, 0.35)}, m.shadow(1)]}>
            <View style={styles.planHead}>
                <View style={[styles.rowIcon, {backgroundColor: withAlpha(BRAND.base, m.isDark ? 0.24 : 0.14)}]}>
                    <Icon name="creation" size={20} color={BRAND.base} />
                </View>
                <View style={{flex: 1}}>
                    <Text style={[styles.rowTitle, {color: m.colors.heading}]}>{t('myday_ai_plan_title')}</Text>
                    <Text style={[styles.rowSub, {color: m.colors.subText}]}>{plan ? plan.headline : t('myday_ai_plan_hint')}</Text>
                </View>
                <Pressable
                    onPress={onRun}
                    disabled={state.busy}
                    accessibilityRole="button"
                    style={({pressed}) => [styles.planBtn, {backgroundColor: BRAND.base, opacity: state.busy ? 0.6 : 1}, pressed && styles.pressed]}
                >
                    {state.busy ? <ActivityIndicator size="small" color="#FFFFFF" /> : <Text style={styles.planBtnText}>{plan ? t('myday_ai_plan_again') : t('myday_ai_plan_run')}</Text>}
                </Pressable>
            </View>
            {!!state.error && <Text style={[styles.draftHint, {color: m.colors.danger, marginTop: 8}]}>{state.error}</Text>}
            {!!plan && (
                <View style={{marginTop: 10, gap: 8}}>
                    {!!plan.summary && <Text style={[styles.planSummary, {color: m.colors.text}]}>{plan.summary}</Text>}
                    {plan.plan.map((step, index) => (
                        <View key={step.key} style={styles.step}>
                            <View style={[styles.stepNo, {backgroundColor: withAlpha(BRAND.base, 0.16)}]}><Text style={[styles.stepNoText, {color: BRAND.base}]}>{index + 1}</Text></View>
                            <View style={{flex: 1}}>
                                <Text style={[styles.stepAction, {color: m.colors.heading}]}>{step.action}</Text>
                                <Text style={[styles.rowSub, {color: m.colors.subText}]} numberOfLines={2}>{[byKey.get(step.key)?.title, step.why].filter(Boolean).join(' — ')}</Text>
                            </View>
                        </View>
                    ))}
                    {plan.tips.map((tip) => (
                        <View key={tip} style={styles.step}>
                            <Icon name="lightbulb-outline" size={16} color={m.colors.muted} />
                            <Text style={[styles.rowSub, {color: m.colors.subText, flex: 1}]}>{tip}</Text>
                        </View>
                    ))}
                </View>
            )}
        </View>
    );
};

/**
 * @param {object} props props.
 * @param {object} props.agenda normalized agenda.
 * @param {object} props.focus normalized focus block (see `normalizeFocus`).
 * @param {(target: {name: string, params: object}) => void} props.onNavigate close the briefing and open a screen.
 * @returns {JSX.Element|null} focus block (null when there is nothing to prioritise).
 */
export default function MyDayFocus({agenda, focus, onNavigate}) {
    const {t, i18n} = useTranslation();
    const ranked = useMemo(() => rankPriorities({agenda, focus}), [agenda, focus]);
    const top = ranked.slice(0, TOP_LIMIT);
    const [planState, setPlanState] = useState({busy: false, error: ''});
    const [plan, setPlan] = useState(null);
    const [replies, setReplies] = useState({});

    const runPlan = useCallback(async () => {
        setPlanState({busy: true, error: ''});

        try {
            setPlan(await analyzeFocusPlan(toPlanItems(ranked, t), i18n?.language));
            setPlanState({busy: false, error: ''});
        } catch (err) {
            setPlanState({busy: false, error: noCredits(err) ? t('myday_ai_no_credits') : describeApiError(err, t('myday_ai_failed'))});
        }
    }, [ranked, t, i18n]);

    const runReply = useCallback(async (row) => {
        const messageId = String(row.ref.id);

        setReplies((prev) => ({...prev, [messageId]: {busy: true}}));

        try {
            const draft = await draftMailReply({messageId, locale: i18n?.language});

            setReplies((prev) => ({...prev, [messageId]: draft ? {draft} : {error: t('myday_ai_failed')}}));
        } catch (err) {
            const credits = noCredits(err);

            setReplies((prev) => ({...prev, [messageId]: {error: credits ? t('myday_ai_no_credits') : describeApiError(err, t('myday_ai_failed')), noCredits: credits}}));
        }
    }, [i18n, t]);

    const openDraft = useCallback((messageId) => {
        const draft = replies[messageId]?.draft;

        if (draft) {
            onNavigate({name: 'MailComposeScreen', params: draftComposeParams(draft)});
        }
    }, [replies, onNavigate]);

    if (ranked.length === 0) {
        return null;
    }

    const renderRow = (row) => (
        <FocusRow
            key={row.key}
            row={row}
            onOpen={onNavigate}
            reply={replies[String(row.ref?.id)]}
            onReply={() => runReply(row)}
            onOpenDraft={() => openDraft(String(row.ref.id))}
        />
    );
    const section = (source, icon, titleKey, total) => {
        const rows = ranked.filter((r) => r.source === source);

        if (!rows.length) {
            return null;
        }

        return (
            <>
                <SectionTitle icon={icon} title={t(titleKey)} count={Math.max(total || 0, rows.length)} />
                {rows.slice(0, SECTION_LIMIT).map(renderRow)}
                {rows.length > SECTION_LIMIT && <Text style={styles.more}>{t('myday_more', {count: rows.length - SECTION_LIMIT})}</Text>}
            </>
        );
    };

    return (
        <>
            <SectionTitle icon="target" title={t('myday_focus_title')} count={ranked.length} />
            {top.map(renderRow)}
            <PlanCard ranked={ranked} plan={plan} state={planState} onRun={runPlan} />
            {section('project', 'briefcase-outline', 'myday_focus_projects', focus?.totals?.projects)}
            {section('lead', 'account-search-outline', 'myday_focus_leads', focus?.totals?.leads)}
            {section('customer', 'account-clock-outline', 'myday_focus_customers', focus?.totals?.customers)}
        </>
    );
}

const styles = StyleSheet.create({
    row: {borderRadius: 20, borderWidth: 1, marginBottom: 10, overflow: 'hidden'},
    stripe: {position: 'absolute', left: 0, top: 0, bottom: 0, width: 4},
    rowMain: {flexDirection: 'row', alignItems: 'center', gap: 12, padding: 12, paddingLeft: 16},
    rowIcon: {width: 40, height: 40, borderRadius: 14, alignItems: 'center', justifyContent: 'center'},
    rowTitle: {fontSize: 15, fontWeight: '800'},
    rowSub: {fontSize: 12.5, fontWeight: '700', marginTop: 1},
    meta: {flexDirection: 'row', alignItems: 'center', flexWrap: 'wrap', gap: 6, marginTop: 5},
    time: {fontSize: 12.5, fontWeight: '800'},
    chip: {borderRadius: 999, paddingHorizontal: 8, paddingVertical: 2, maxWidth: 220},
    chipText: {fontSize: 11, fontWeight: '900'},
    aiBtn: {flexDirection: 'row', alignItems: 'center', alignSelf: 'flex-start', gap: 6, marginLeft: 16, marginBottom: 10, borderWidth: 1.2, borderRadius: 999, paddingHorizontal: 12, paddingVertical: 6},
    aiBtnText: {fontSize: 12.5, fontWeight: '900'},
    draft: {marginHorizontal: 12, marginBottom: 12, marginLeft: 16, borderRadius: 14, borderWidth: 1, padding: 12, gap: 6},
    draftLabel: {fontSize: 10.5, fontWeight: '900', letterSpacing: 0.8},
    draftSubject: {fontSize: 14, fontWeight: '800'},
    draftBody: {fontSize: 13.5, lineHeight: 19},
    draftActions: {flexDirection: 'row', alignItems: 'center', gap: 14, marginTop: 4},
    draftCta: {flexDirection: 'row', alignItems: 'center', gap: 6, borderRadius: 12, paddingHorizontal: 12, paddingVertical: 9},
    draftCtaText: {color: '#FFFFFF', fontSize: 13, fontWeight: '900'},
    draftHint: {fontSize: 12, fontWeight: '600'},
    link: {fontSize: 13, fontWeight: '800'},
    plan: {borderRadius: 20, borderWidth: 1.2, padding: 12, marginBottom: 10},
    planHead: {flexDirection: 'row', alignItems: 'center', gap: 12},
    planBtn: {borderRadius: 12, paddingHorizontal: 12, paddingVertical: 9, minWidth: 74, alignItems: 'center'},
    planBtnText: {color: '#FFFFFF', fontSize: 12.5, fontWeight: '900'},
    planSummary: {fontSize: 13.5, lineHeight: 19, fontWeight: '600'},
    step: {flexDirection: 'row', alignItems: 'flex-start', gap: 10},
    stepNo: {width: 22, height: 22, borderRadius: 11, alignItems: 'center', justifyContent: 'center'},
    stepNoText: {fontSize: 12, fontWeight: '900'},
    stepAction: {fontSize: 14, fontWeight: '800'},
    more: {textAlign: 'center', fontSize: 12.5, fontWeight: '800', opacity: 0.6, marginBottom: 8},
    pressed: {opacity: 0.72, transform: [{scale: 0.98}]},
});
